<template>
  <div>
    <section v-if="loading || hasData">
      <page-sub-header
        :title="$t('analytics.app.sections.track')"
        :subtitle="isDataReady ? timeSeriesSubtitle : undefined"
        class="mt-4"
      >
        <template #header>
          <div class="d-flex flex-row align-items-center justify-content-end">
            <date-range-picker
              v-if="portalUpdatesJan2026Enabled"
              :from="dateFrom"
              :to="dateTo"
              :earliest="earliestAvailable"
              class="mr-3"
              @update:from="dateFrom = $event"
              @update:to="dateTo = $event"
            />
            <app-input
              v-model="timeSeries"
              :options="TIME_SERIES_VALUES"
              type="radio"
              buttons
              button-variant="outline-primary"
              class="text-uppercase mb-0"
              size="sm"
            />
            <export-button
              v-if="isDataReady && !portalUpdatesJan2026Enabled"
              :file-name="`track-metrics-${timeSeries}`"
              :export-data="exportData"
              :export-file-rows="exportFileRows"
              :export-headers="exportHeaders"
              class="btn mt-0"
            />
          </div>
        </template>
      </page-sub-header>
      <sme-card v-if="loading">
        <app-loading />
      </sme-card>
      <template v-else>
        <chart-with-companion-kpis
          v-if="timeGroupedCountDataset?.values?.length"
          chart-title="Budget viewers over time"
          chart-subtitle="Number of employees viewing Budget"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedCountDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCountDataset.name } } },
              ...createCountWithPercentageTooltip(timeGroupedPercentageDataset),
            }"
            fill-gradient
          />
          <template #companion>
            <div class="stats-combined mb-3">
              <app-stat
                :stat="lastCountValue"
                :previous-stat="prevCountValue"
                :formatter="toInteger"
                description="Employees Viewing Budget"
                background-color="core-1-lighten-90"
                color="default"
                pdf-kpi
              />
              <app-stat
                :stat="lastPercentageValue"
                :previous-stat="prevPercentage"
                :formatter="toNumber"
                suffix="%"
                description="Budget Percentage"
                background-color="core-1-lighten-90"
                color="default"
                pdf-kpi
              />
            </div>
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ toInteger(lastCountValue) }} employees ({{ lastPercentageValue }}%) viewed
              Budget.
              {{ countPrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>
      </template>
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDateRangeSelection from '@/state/composables/useDateRangeSelection';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { TIME_SERIES_VALUES, createCountWithPercentageTooltip, getInitialAspectRatio } from '@/utils/chart';
import { toInteger, toNumber } from '@/utils/common';
import ExportButton from '@/components/ExportButton.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';

const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();
const { from: dateFrom, to: dateTo, apiFrom, apiTo } = useDateRangeSelection();

const loading = ref(true);
const timeDataset = ref(null);
const countDataset = ref(null);
const percentageDataset = ref(null);
const earliestAvailable = ref(null);

const {
  timeSeries,
  timeSeriesSubtitle,
  timeGroupDataset,
  timeGroupTitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const timeGroupedCountDataset = computed(() => datasetToTimeGroupedDataset(countDataset));
const timeGroupedPercentageDataset = computed(() => datasetToTimeGroupedDataset(percentageDataset));
const lastCountValue = computed(() => timeGroupedCountDataset.value?.calculated.last);
const prevCountValue = computed(() => timeGroupedCountDataset.value?.calculated.prev);
const lastPercentageValue = computed(() => timeGroupedPercentageDataset.value?.calculated.last);
const prevPercentage = computed(() => timeGroupedPercentageDataset.value?.calculated.prev);
const countPrevDiffText = computed(() => getPrevDiffText(timeGroupedCountDataset));

watch([divisionId, timeSeries, dateFrom, dateTo], () => getTrackMetrics());

const getTrackMetrics = async () => {
  loading.value = true;
  // Drop the old scope's data up front: a failed or incomplete refetch after a
  // division or date change would otherwise leave its numbers on screen under
  // the new scope.
  timeDataset.value = null;
  countDataset.value = null;
  percentageDataset.value = null;

  try {
    const { results, earliest_available } = await ApiClient.getTrackMetrics(
      timeSeries.value,
      divisionId.value,
      apiFrom.value,
      apiTo.value,
    );

    earliestAvailable.value = earliest_available ?? null;

    const time = results?.find(result => result.id === 'time');
    const track = results?.find(result => result.id === 'track_users');
    const enrolled = results?.find(result => result.id === 'enrolled');

    if (time?.values && track?.values && enrolled?.values) {
      timeDataset.value = time;
      countDataset.value = {
        ...track,
        name: 'Number of employees',
      };
      percentageDataset.value = {
        name: 'Percentage (%)',
        values: enrolled.values.map((value, index) =>
          value ? Number(toNumber((track.values[index] / value) * 100)) : 0,
        ),
      };
    }
  } catch (error) {
    console.error('Failed to fetch track metrics:', error);
  }

  loading.value = false;
};

onBeforeMount(() => {
  getTrackMetrics();
});

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values && countDataset.value?.values && percentageDataset.value?.values && !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    viewers: countDataset.value.values[index],
    percentage: percentageDataset.value.values[index],
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return ['Date', countDataset.value.name, percentageDataset.value.name];
});

const exportFileRows = item => [item.time, item.viewers, item.percentage];

const hasData = computed(() => !loading.value && !!timeGroupedCountDataset.value?.values?.length);

function getExportTable() {
  if (!isDataReady.value) {
    return null;
  }
  return {
    headers: exportHeaders.value,
    rows: exportData.value.map(exportFileRows),
  };
}

defineExpose({ hasData, getExportTable });
</script>

<style lang="scss" scoped>
.stats-combined {
  display: flex;

  :deep(.app-stat) {
    flex: 1;
    height: auto; // Override height: 100% from AppStat to allow flexbox stretch
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}
</style>
